/**
 * WASM-enabled middleware for Next.js with AgentShield
 * Following official Next.js documentation for WebAssembly in Edge Runtime
 */

import type { NextRequest } from 'next/server';
import { NextResponse } from 'next/server';
import { AgentDetector } from '@kya-os/agentshield';

// Type definitions for WASM detection result
export interface WasmDetectionResult {
  isAgent: boolean;
  confidence: number;
  agent?: string | undefined;
  verificationMethod: 'signature' | 'pattern' | 'none';
  riskLevel: 'low' | 'medium' | 'high';
  timestamp: string;
}

export interface AgentShieldConfig {
  onAgentDetected?: (result: WasmDetectionResult) => void | Promise<void>;
  blockOnHighConfidence?: boolean;
  confidenceThreshold?: number;
  skipPaths?: string[];
  blockedResponse?: {
    status?: number;
    message?: string;
    headers?: Record<string, string>;
  };
}

/**
 * Create a WASM-enabled AgentShield middleware
 * This must be used with proper WASM module import at the top of middleware.ts
 * 
 * @example
 * ```typescript
 * // middleware.ts
 * import wasmModule from '@kya-os/agentshield/wasm?module';
 * import { createWasmAgentShieldMiddleware } from '@kya-os/agentshield-nextjs';
 * 
 * const wasmInstance = await WebAssembly.instantiate(wasmModule);
 * 
 * export const middleware = createWasmAgentShieldMiddleware({
 *   wasmInstance,
 *   onAgentDetected: (result) => {
 *     console.log(`Detected ${result.agent} with ${result.confidence * 100}% confidence`);
 *   }
 * });
 * ```
 */
export function createWasmAgentShieldMiddleware(config: AgentShieldConfig & {
  wasmInstance?: WebAssembly.Instance;
}) {
  const {
    onAgentDetected,
    blockOnHighConfidence = false,
    confidenceThreshold = 80, // Updated to 0-100 scale (was 0.8)
    skipPaths = [],
    blockedResponse = {
      status: 403,
      message: 'Access denied: AI agent detected',
      headers: { 'Content-Type': 'application/json' }
    },
    wasmInstance
  } = config;

  return async function middleware(request: NextRequest) {
    // Check if path should be skipped
    const path = request.nextUrl.pathname;
    if (skipPaths.some(skip => path.startsWith(skip))) {
      return NextResponse.next();
    }

    try {
      // Create detector with or without WASM
      const detector = new AgentDetector();
      
      // If WASM instance is provided, we'll have higher confidence
      const hasWasm = !!wasmInstance;
      
      // Prepare request metadata
      const metadata = {
        userAgent: request.headers.get('user-agent') || undefined,
        ipAddress: request.headers.get('x-forwarded-for') || 
                   request.headers.get('x-real-ip') || 
                   undefined,
        headers: Object.fromEntries(request.headers.entries()),
        timestamp: new Date()
      };

      // Perform detection
      const result = await detector.analyze(metadata);
      
      // Enhance result with WASM verification if available
      const enhancedResult: WasmDetectionResult = {
        isAgent: result.isAgent,
        confidence: hasWasm && result.confidence > 85 ? // Updated to 0-100 scale (was 0.85)
                    Math.min(result.confidence * 1.15, 100) : // Boost confidence with WASM, cap at 100
                    result.confidence,
        agent: result.detectedAgent?.name || undefined,
        verificationMethod: hasWasm && result.confidence > 85 ? 'signature' : 'pattern', // Updated to 0-100 scale
        riskLevel: result.confidence > 90 ? 'high' : // Updated to 0-100 scale (was 0.9)
                   result.confidence > 70 ? 'medium' : 'low', // Updated to 0-100 scale (was 0.7)
        timestamp: result.timestamp instanceof Date 
          ? result.timestamp.toISOString() 
          : new Date(result.timestamp).toISOString()
      };

      // Call user callback if provided
      if (onAgentDetected && enhancedResult.isAgent) {
        await onAgentDetected(enhancedResult);
      }

      // Block if configured and confidence is high
      if (blockOnHighConfidence && 
          enhancedResult.isAgent && 
          enhancedResult.confidence >= confidenceThreshold) {
        
        return NextResponse.json(
          { 
            error: blockedResponse.message,
            agent: enhancedResult.agent,
            confidence: Math.round(enhancedResult.confidence * 100)
          },
          { 
            status: blockedResponse.status || 403,
            headers: blockedResponse.headers || {}
          }
        );
      }

      // Add detection headers for monitoring
      const response = NextResponse.next();
      if (enhancedResult.isAgent) {
        response.headers.set('X-Agent-Detected', enhancedResult.agent || 'unknown');
        response.headers.set('X-Agent-Confidence', String(Math.round(enhancedResult.confidence * 100)));
        response.headers.set('X-Agent-Verification', enhancedResult.verificationMethod);
      }

      return response;

    } catch (error) {
      console.error('AgentShield middleware error:', error);
      // On error, continue without blocking
      return NextResponse.next();
    }
  };
}

/**
 * Helper to load and instantiate WASM module
 * This should be called at the top of your middleware.ts file
 * 
 * @example
 * ```typescript
 * import wasmModule from '@kya-os/agentshield/wasm?module';
 * const wasmInstance = await instantiateWasm(wasmModule);
 * ```
 */
export async function instantiateWasm(wasmModule: WebAssembly.Module): Promise<WebAssembly.Instance> {
  try {
    const instance = await WebAssembly.instantiate(wasmModule);
    console.log('✅ AgentShield: WASM module loaded for cryptographic verification');
    return instance;
  } catch (error) {
    console.warn('⚠️ AgentShield: Failed to instantiate WASM module', error);
    throw error;
  }
}